import { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import {
  LogOut, Send, Users, Mail, Trash2, Eye, EyeOff,
  CheckCircle, AlertTriangle, Loader2, ChevronDown, ChevronUp, RefreshCw,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import AdminSubNav from "../components/AdminSubNav";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/* ── Delivery status helpers ── */
const statusConfig = {
  sending:   { label: "Sending",   color: "bg-blue-100 text-blue-700",   icon: Loader2 },
  completed: { label: "Delivered", color: "bg-green-100 text-green-700", icon: CheckCircle },
  partial:   { label: "Partial",   color: "bg-amber-100 text-amber-700", icon: AlertTriangle },
  failed:    { label: "Failed",    color: "bg-red-100 text-red-700",     icon: AlertTriangle },
};

const StatusBadge = ({ status }) => {
  const cfg = statusConfig[status] || statusConfig.completed;
  const Icon = cfg.icon;
  return (
    <span
      data-testid={`status-badge-${status}`}
      className={`inline-flex items-center gap-1 text-xs font-medium px-2 py-0.5 rounded ${cfg.color}`}
    >
      <Icon className={`w-3 h-3 ${status === "sending" ? "animate-spin" : ""}`} />
      {cfg.label}
    </span>
  );
};

const ProgressBar = ({ sent, failed, total }) => {
  if (!total) return null;
  const pct = Math.round(((sent + failed) / total) * 100);
  const sentPct = Math.round((sent / total) * 100);
  const failedPct = Math.round((failed / total) * 100);
  return (
    <div data-testid="delivery-progress" className="mt-2">
      <div className="flex items-center justify-between text-xs text-slate-500 mb-1">
        <span>{sent} sent{failed > 0 ? `, ${failed} failed` : ""} / {total}</span>
        <span>{pct}%</span>
      </div>
      <div className="h-1.5 bg-slate-100 rounded-full overflow-hidden flex">
        {sentPct > 0 && <div className="bg-green-500 transition-all duration-500" style={{ width: `${sentPct}%` }} />}
        {failedPct > 0 && <div className="bg-red-400 transition-all duration-500" style={{ width: `${failedPct}%` }} />}
      </div>
    </div>
  );
};

/* ── Expandable failed emails ── */
const FailedEmails = ({ emails, details }) => {
  const [open, setOpen] = useState(false);
  const rows = details?.length
    ? details
    : (emails || []).map((e) => ({ email: e, reason: null }));
  if (!rows.length) return null;
  return (
    <div className="mt-2">
      <button
        onClick={() => setOpen(!open)}
        className="flex items-center gap-1 text-xs text-red-600 hover:text-red-700 font-medium"
        data-testid="toggle-failed-emails"
      >
        {open ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
        {rows.length} failed email{rows.length > 1 ? "s" : ""}
      </button>
      {open && (
        <ul className="mt-1 text-xs text-slate-600 space-y-0.5 pl-4 list-disc max-h-32 overflow-y-auto">
          {rows.map((r) => (
            <li key={r.email} data-testid={`failed-email-${r.email}`}>
              {r.email}
              {r.reason && <span className="text-red-500"> — {r.reason}</span>}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

/* ── System messages (auto welcome, etc.) ── */
const SystemMessagesSection = () => {
  const [msgs, setMsgs] = useState([]);
  const [total, setTotal] = useState(0);
  const [readCount, setReadCount] = useState(0);
  useEffect(() => {
    const run = async () => {
      try {
        const token = localStorage.getItem("access_token");
        const resp = await axios.get(`${API}/admin/messages/system`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setMsgs(resp.data.messages || []);
        setTotal(resp.data.total || 0);
        setReadCount(resp.data.read_count || 0);
      } catch (e) {
        // silently ignore
      }
    };
    run();
  }, []);

  if (!msgs.length) return null;
  const readRate = total > 0 ? Math.round((readCount / total) * 100) : 0;
  return (
    <div className="mt-10" data-testid="system-messages-section">
      <div className="flex items-center gap-3 mb-1">
        <h3 className="text-lg font-semibold" style={{ fontFamily: "Fraunces, serif" }}>
          System Messages
        </h3>
        <span
          className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
            readRate >= 50 ? "bg-emerald-50 text-emerald-600" : "bg-amber-50 text-amber-600"
          }`}
          data-testid="system-messages-read-rate"
          title={`${readCount} of ${total} messages opened`}
        >
          {readRate}% read rate
        </span>
      </div>
      <p className="text-xs text-slate-500 mb-4">
        Automatic in-app messages sent by the platform (e.g. the welcome message new users
        receive on registration). {readCount} of {total} opened by recipients.
      </p>
      <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 border-b border-slate-200">
            <tr>
              <th className="px-4 py-2 text-left text-xs font-medium text-slate-600 uppercase tracking-wider">Recipient</th>
              <th className="px-4 py-2 text-left text-xs font-medium text-slate-600 uppercase tracking-wider">Subject</th>
              <th className="px-4 py-2 text-left text-xs font-medium text-slate-600 uppercase tracking-wider">Sent</th>
              <th className="px-4 py-2 text-left text-xs font-medium text-slate-600 uppercase tracking-wider">Status</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {msgs.map((m) => (
              <tr key={m.id} data-testid={`system-message-row-${m.id}`}>
                <td className="px-4 py-2">
                  <div className="text-slate-900">{m.to_name || "—"}</div>
                  <div className="text-xs text-slate-500">{m.to_email}</div>
                </td>
                <td className="px-4 py-2 text-slate-700 max-w-xs truncate">{m.subject}</td>
                <td className="px-4 py-2 text-xs text-slate-500 whitespace-nowrap">
                  {m.sent_at ? new Date(m.sent_at).toLocaleString() : "—"}
                </td>
                <td className="px-4 py-2">
                  {m.is_read ? (
                    <span className="inline-flex items-center px-2 py-0.5 rounded text-xs bg-emerald-50 text-emerald-600">
                      Read{m.read_at ? ` · ${new Date(m.read_at).toLocaleDateString()}` : ""}
                    </span>
                  ) : (
                    <span className="inline-flex items-center px-2 py-0.5 rounded text-xs bg-slate-100 text-slate-500">
                      Unread
                    </span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

/* ── Main page ── */
const AdminAnnouncements = ({ user, setUser }) => {
  const [subject, setSubject] = useState("");
  const [message, setMessage] = useState("");
  const [target, setTarget] = useState("all");
  const [specificEmails, setSpecificEmails] = useState("");
  const [publishPublic, setPublishPublic] = useState(true);
  const [sending, setSending] = useState(false);
  const [previewCount, setPreviewCount] = useState(null);
  const [announcements, setAnnouncements] = useState([]);
  const pollRef = useRef(null);
  const navigate = useNavigate();

  const handleLogout = () => {
    localStorage.removeItem("access_token");
    localStorage.removeItem("user");
    setUser(null);
    navigate("/");
  };

  /* ── Fetch announcements ── */
  const fetchAnnouncements = useCallback(async () => {
    try {
      const token = localStorage.getItem("access_token");
      const resp = await axios.get(`${API}/admin/announcements`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setAnnouncements(resp.data || []);
    } catch (err) {
      console.error("Failed to fetch announcements:", err);
    }
  }, []);

  useEffect(() => { fetchAnnouncements(); }, [fetchAnnouncements]);

  /* ── Auto-poll while any announcement is "sending" ── */
  useEffect(() => {
    const hasSending = announcements.some((a) => a.status === "sending");
    if (hasSending && !pollRef.current) {
      pollRef.current = setInterval(fetchAnnouncements, 3000);
    }
    if (!hasSending && pollRef.current) {
      clearInterval(pollRef.current);
      pollRef.current = null;
    }
    return () => { if (pollRef.current) clearInterval(pollRef.current); };
  }, [announcements, fetchAnnouncements]);

  /* ── Preview count ── */
  useEffect(() => {
    if (target === "specific") { setPreviewCount(null); return; }
    const fetchCount = async () => {
      try {
        const token = localStorage.getItem("access_token");
        const resp = await axios.get(`${API}/admin/announcements/preview-count`, {
          headers: { Authorization: `Bearer ${token}` },
          params: { target },
        });
        setPreviewCount(resp.data.count);
      } catch { setPreviewCount(null); }
    };
    fetchCount();
  }, [target]);

  /* ── Send ── */
  const handleSend = async () => {
    if (!subject.trim()) { toast.error("Please enter a subject"); return; }
    if (!message.trim()) { toast.error("Please enter a message"); return; }
    if (target === "specific" && !specificEmails.trim()) { toast.error("Please enter email addresses"); return; }

    const confirmMsg = target === "specific"
      ? `Send announcement to ${specificEmails.split(/[,\n]/).filter((e) => e.trim()).length} email(s)?`
      : `Send announcement to ${previewCount || "all"} user(s)?`;
    if (!window.confirm(confirmMsg)) return;

    setSending(true);
    try {
      const token = localStorage.getItem("access_token");
      const payload = {
        subject: subject.trim(),
        message: message.trim(),
        target_audience: target,
        is_public: publishPublic,
      };
      if (target === "specific") {
        payload.specific_emails = specificEmails.split(/[,\n]/).map((e) => e.trim()).filter((e) => e);
      }
      const resp = await axios.post(`${API}/admin/announcements/send`, payload, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (resp.data.success) {
        toast.success(resp.data.message || `Queued for ${resp.data.queued_count} user(s)`);
        setSubject(""); setMessage(""); setSpecificEmails("");
        fetchAnnouncements();
      } else {
        toast.error(resp.data.message || "No matching users found");
      }
    } catch (err) {
      console.error("Announcement send error:", err);
      const detail = err.response?.data?.detail;
      const msg = err.response?.data?.message;
      const status = err.response?.status;
      if (detail) {
        toast.error(detail);
      } else if (msg) {
        toast.error(msg);
      } else if (status) {
        toast.error(`Server error (${status}). Please try again.`);
      } else {
        toast.error("Network error. Please check your connection and try again.");
      }
    } finally { setSending(false); }
  };

  const toggleAnnouncement = async (id, currentActive) => {
    try {
      const token = localStorage.getItem("access_token");
      await axios.patch(`${API}/admin/announcements/${id}?is_active=${!currentActive}`, {}, {
        headers: { Authorization: `Bearer ${token}` },
      });
      fetchAnnouncements();
      toast.success(currentActive ? "Announcement hidden" : "Announcement visible");
    } catch { toast.error("Failed to update announcement"); }
  };

  const retryAnnouncement = async (id) => {
    if (!window.confirm("Retry sending to all failed recipients?")) return;
    try {
      const token = localStorage.getItem("access_token");
      const resp = await axios.post(`${API}/admin/announcements/${id}/retry`, {}, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (resp.data.success) {
        toast.success(resp.data.message || "Retrying failed emails...");
        fetchAnnouncements();
      } else {
        toast.error(resp.data.message || "Nothing to retry");
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to retry announcement");
    }
  };

  const deleteAnnouncement = async (id) => {
    if (!window.confirm("Delete this announcement permanently?")) return;
    try {
      const token = localStorage.getItem("access_token");
      await axios.delete(`${API}/admin/announcements/${id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      fetchAnnouncements();
      toast.success("Announcement deleted");
    } catch { toast.error("Failed to delete announcement"); }
  };

  /* ── Target label helper ── */
  const targetLabel = (t) =>
    ({ all: "All Users", subscribers: "Subscribers", trial: "Trial", specific: "Specific" }[t] || t);

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Nav */}
      <nav className="bg-slate-900 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <h1 className="text-xl font-semibold" style={{ fontFamily: "Fraunces, serif" }}>Admin Dashboard</h1>
            <div className="flex items-center space-x-4">
              <Button onClick={() => navigate("/")} variant="ghost" className="text-white hover:text-white hover:bg-slate-800">Public Site</Button>
              <Button onClick={handleLogout} variant="ghost" size="sm" className="text-white hover:text-white hover:bg-slate-800"><LogOut className="w-4 h-4" /></Button>
            </div>
          </div>
        </div>
      </nav>

      <AdminSubNav user={user} activeTab="announcements" />

      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* ── Compose form ── */}
        <div className="mb-6">
          <h2 className="text-2xl font-bold" style={{ fontFamily: "Fraunces, serif" }}>Send Announcement</h2>
          <p className="text-sm text-slate-500 mt-1">Send email announcements to your users</p>
        </div>

        <div className="bg-white border border-slate-200 rounded-lg p-6 space-y-6">
          {/* Target Audience */}
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2"><Users className="w-4 h-4 inline mr-1" />Target Audience</label>
            <select value={target} onChange={(e) => setTarget(e.target.value)} className="w-full border border-slate-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" data-testid="target-select">
              <option value="all">All Users</option>
              <option value="subscribers">Active Subscribers Only</option>
              <option value="trial">Trial Users Only</option>
              <option value="specific">Specific Emails</option>
            </select>
            {target !== "specific" && previewCount !== null && (
              <p className="text-xs text-slate-500 mt-1">{previewCount} user{previewCount !== 1 ? "s" : ""} will receive this announcement</p>
            )}
          </div>

          {target === "specific" && (
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">Email Addresses</label>
              <textarea value={specificEmails} onChange={(e) => setSpecificEmails(e.target.value)} placeholder="Enter email addresses (one per line or comma-separated)" rows={3} className="w-full border border-slate-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" data-testid="specific-emails" />
              <p className="text-xs text-slate-500 mt-1">{specificEmails.split(/[,\n]/).filter((e) => e.trim()).length} email(s) entered</p>
            </div>
          )}

          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2"><Mail className="w-4 h-4 inline mr-1" />Subject</label>
            <input type="text" value={subject} onChange={(e) => setSubject(e.target.value)} placeholder="Enter email subject" className="w-full border border-slate-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" data-testid="subject-input" />
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">Message</label>
            <textarea value={message} onChange={(e) => setMessage(e.target.value)} placeholder="Enter your announcement message..." rows={8} className="w-full border border-slate-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" data-testid="message-input" />
            <p className="text-xs text-slate-500 mt-1">{message.length} characters</p>
          </div>

          <div className="flex items-center gap-2">
            <input type="checkbox" id="publishPublic" checked={publishPublic} onChange={(e) => setPublishPublic(e.target.checked)} className="w-4 h-4 rounded border-slate-300 text-blue-600 focus:ring-blue-500" />
            <label htmlFor="publishPublic" className="text-sm text-slate-700">Also publish on public announcements page</label>
          </div>

          {(subject || message) && (
            <div className="border border-slate-200 rounded-md p-4 bg-slate-50">
              <p className="text-xs font-medium text-slate-500 mb-2">Preview</p>
              <div className="bg-white border border-slate-200 rounded p-4">
                <p className="font-medium text-slate-900">{subject || "(No subject)"}</p>
                <hr className="my-2" />
                <p className="text-sm text-slate-700 whitespace-pre-wrap">{message || "(No message)"}</p>
              </div>
            </div>
          )}

          <div className="flex justify-end pt-4 border-t border-slate-200">
            <Button onClick={handleSend} disabled={sending || !subject.trim() || !message.trim()} className="bg-blue-600 hover:bg-blue-700 text-white" data-testid="send-btn">
              {sending ? <>Sending...</> : <><Send className="w-4 h-4 mr-2" />Send Announcement</>}
            </Button>
          </div>
        </div>

        {/* ── Delivery Status Dashboard ── */}
        {announcements.length > 0 && (
          <div className="mt-10" data-testid="delivery-dashboard">
            <h3 className="text-lg font-semibold mb-4" style={{ fontFamily: "Fraunces, serif" }}>
              Delivery Dashboard
            </h3>
            <div className="bg-white border border-slate-200 rounded-lg divide-y divide-slate-200">
              {announcements.map((ann) => (
                <div key={ann.id} className="p-4" data-testid={`announcement-row-${ann.id}`}>
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1 min-w-0">
                      {/* Row 1: badges */}
                      <div className="flex flex-wrap items-center gap-2 mb-1">
                        <StatusBadge status={ann.status || "completed"} />
                        {ann.is_public && (
                          <span className={`text-xs px-2 py-0.5 rounded ${ann.is_active ? "bg-emerald-50 text-emerald-600" : "bg-slate-100 text-slate-500"}`}>
                            {ann.is_active ? "Public" : "Hidden"}
                          </span>
                        )}
                        <span className="text-xs text-slate-400 font-medium">{targetLabel(ann.target)}</span>
                        <span className="text-xs text-slate-400">{new Date(ann.created_at).toLocaleString()}</span>
                      </div>
                      {/* Row 2: content */}
                      <p className="font-medium text-slate-900">{ann.subject}</p>
                      <p className="text-sm text-slate-600 line-clamp-2">{ann.message}</p>
                      {/* Row 3: progress */}
                      <ProgressBar
                        sent={ann.sent_count ?? 0}
                        failed={ann.failed_count ?? 0}
                        total={ann.total_recipients ?? ann.sent_count ?? 0}
                      />
                      {/* Row 4: failed emails */}
                      <FailedEmails emails={ann.failed_emails} details={ann.failed_details} />
                    </div>
                    {/* Actions */}
                    <div className="flex items-center gap-1 shrink-0">
                      {(ann.status === "failed" || ann.status === "partial") && ann.failed_count > 0 && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => retryAnnouncement(ann.id)}
                          className="text-blue-600 hover:text-blue-700 hover:bg-blue-50"
                          title="Retry failed emails"
                          data-testid={`retry-btn-${ann.id}`}
                        >
                          <RefreshCw className="w-4 h-4" />
                        </Button>
                      )}
                      {ann.is_public && (
                        <Button variant="ghost" size="sm" onClick={() => toggleAnnouncement(ann.id, ann.is_active)} title={ann.is_active ? "Hide from public" : "Show publicly"}>
                          {ann.is_active ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                        </Button>
                      )}
                      <Button variant="ghost" size="sm" onClick={() => deleteAnnouncement(ann.id)} className="text-red-600 hover:text-red-700" title="Delete">
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        <SystemMessagesSection />
      </div>
    </div>
  );
};

export default AdminAnnouncements;
